import React, { useState } from 'react';
import type { Workspace } from '../../types';
import SchedulerToggleModal from './SchedulerToggleModal';
import './SchedulerHeader.css';

interface SchedulerHeaderProps {
  workspace: Workspace;
  onToggleScheduler: (_enabled: boolean) => void;
  onRescheduleAll?: () => void;
  onReconcileTasks?: () => void;
  isReconcileLoading?: boolean;
  reconcileSummary?: string | null;
  nextActionTime?: string;
  onNavigateToSettings?: () => void;
}

function SchedulerHeader({
  workspace,
  onToggleScheduler,
  onRescheduleAll,
  onReconcileTasks,
  isReconcileLoading,
  reconcileSummary,
  nextActionTime,
  onNavigateToSettings,
}: SchedulerHeaderProps) {
  const schedulerEnabled = workspace.scheduler_enabled ?? true;
  const [showModal, setShowModal] = useState(false);
  const [showReconcileInfo, setShowReconcileInfo] = useState(false);

  const handleToggle = () => {
    setShowModal(true);
  };

  const handleConfirmToggle = () => {
    setShowModal(false);
    onToggleScheduler(!schedulerEnabled);
  };

  const handleCancelToggle = () => {
    setShowModal(false);
  };

  const formatTimeUntil = (isoDate: string): string => {
    const now = new Date();
    const target = new Date(isoDate);
    const diffMs = target.getTime() - now.getTime();

    if (diffMs < 0) return 'Overdue';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `In ${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `In ${hours} hour${hours > 1 ? 's' : ''}`;
    return 'Less than 1 hour';
  };

  return (
    <>
      <SchedulerToggleModal
        isOpen={showModal}
        currentState={schedulerEnabled}
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
      />
      <div className="scheduler-header">
        <div className="scheduler-controls">
          <div className="control-group">
            <label className="toggle-label">
              Scheduler
              <button
                className={`toggle-switch ${schedulerEnabled ? 'on' : 'off'}`}
                onClick={handleToggle}
                aria-label={schedulerEnabled ? 'Disable scheduler' : 'Enable scheduler'}
              >
                <span className="toggle-slider" />
              </button>
              <span className={`toggle-status ${schedulerEnabled ? 'enabled' : 'disabled'}`}>
                {schedulerEnabled ? 'On' : 'Off'}
              </span>
            </label>
          </div>

          <div className="scheduler-actions">
            {onRescheduleAll && (
              <button className="btn btn-secondary btn-sm" onClick={onRescheduleAll}>
                Reschedule All
              </button>
            )}

            {onReconcileTasks && (
              <div className="reconcile-action-group">
                <button
                  className="btn btn-secondary btn-sm scheduler-reconcile-btn"
                  onClick={onReconcileTasks}
                  disabled={isReconcileLoading}
                  title="Scan manual content/media/publish progress and sync task statuses"
                >
                  {isReconcileLoading ? 'Syncing Manual Progress...' : 'Sync Manual Progress to Tasks'}
                </button>
                <button
                  type="button"
                  className="reconcile-info-toggle"
                  aria-label={showReconcileInfo ? 'Hide sync info' : 'Show sync info'}
                  aria-expanded={showReconcileInfo}
                  onClick={() => setShowReconcileInfo((prev) => !prev)}
                  title="What does this button do?"
                >
                  i
                </button>
              </div>
            )}
          </div>
        </div>

        {onReconcileTasks && showReconcileInfo && (
          <div className="reconcile-help" role="note" aria-live="polite">
            <strong>What this does:</strong> checks your existing content, generated media, and
            already-published posts, then updates matching scheduler tasks to <strong>Done</strong>.
            It does not create, edit, or publish content.
            {reconcileSummary && <div className="reconcile-help__summary">Last sync: {reconcileSummary}</div>}
          </div>
        )}

        {nextActionTime && (
          <div className="next-action-indicator">
            <span className="next-action-label">Next action:</span>
            <span className="next-action-time">{formatTimeUntil(nextActionTime)}</span>
          </div>
        )}

        {workspace.days_before_item !== undefined && workspace.days_before_media !== undefined && (
          <div className="scheduler-config">
            <div className="config-item">
              <span className="config-label">Content creation:</span>
              <span className="config-value">{workspace.days_before_item} days before publish</span>
            </div>
            <div className="config-item">
              <span className="config-label">Media generation:</span>
              <span className="config-value">
                {workspace.days_before_media} days before publish
              </span>
            </div>
            {onNavigateToSettings && (
              <button
                type="button"
                className="scheduler-settings-shortcut"
                onClick={onNavigateToSettings}
              >
                Edit in workspace settings
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default SchedulerHeader;
